import { Menu, MenuButton } from '@headlessui/react';
import { Check, ChevronDown, Globe, LogOut, MapPin, Plus, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { LANGUAGE_NATIVE_NAMES, type Language } from '@lightbm/shared';
import { useAppContext } from '@/app/app-context';
import { useLocationScope } from '@/app/location-context';
import { Avatar } from '@/components/ui/Avatar';
import { MenuDivider, MenuEntry, MenuPanel } from '@/components/ui/Menu';
import { AlertsBell } from '@/features/alerts/Bell';
import { authApi } from '@/features/auth/api';
import { useAuth, useCurrentUser } from '@/features/auth/auth-context';
import { OPERATIONS, shortcutPath } from '@/features/operations/operations';
import { setLanguage } from '@/i18n';
import { useErrorText } from '@/i18n/messages';
import { cn } from '@/lib/cn';

const topButton =
  'flex h-12 items-center gap-2 rounded-control border border-line-strong bg-surface px-3.5 text-ink transition-colors hover:bg-ground data-open:bg-ground';

/** "All locations" switcher (USR-08: only the user's locations are listed). */
export function LocationSwitcher() {
  const { t } = useTranslation();
  const { locations, selected, select } = useLocationScope();
  const user = useCurrentUser();
  const canSeeAll = user.locationScope === 'all';

  return (
    <Menu>
      <MenuButton className={cn(topButton, 'max-w-60')}>
        <MapPin className="size-5 shrink-0 text-ink-2" strokeWidth={1.75} />
        <span className="truncate font-medium">{selected?.name ?? t('locations.all')}</span>
        <ChevronDown className="size-4 shrink-0 text-ink-2" />
      </MenuButton>
      <MenuPanel anchor="bottom start" className="max-h-80 overflow-y-auto">
        {canSeeAll && (
          <MenuEntry
            onClick={() => select(null)}
            icon={selected ? <span className="size-[18px]" /> : <Check />}
          >
            {t('locations.all')}
          </MenuEntry>
        )}
        {locations.map((location) => (
          <MenuEntry
            key={location.id}
            onClick={() => select(location.id)}
            icon={selected?.id === location.id ? <Check /> : <span className="size-[18px]" />}
          >
            {location.name}
          </MenuEntry>
        ))}
        {locations.length === 0 && (
          <p className="px-3 py-2.5 text-sm text-muted">{t('locations.none')}</p>
        )}
      </MenuPanel>
    </Menu>
  );
}

/** Shortcut to every daily operation. */
export function NewOperationMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canAny } = useAuth();
  const available = OPERATIONS.filter((operation) => canAny(...operation.anyOf));
  if (available.length === 0) return null;

  return (
    <Menu>
      <MenuButton
        className="flex h-12 items-center gap-2 rounded-control bg-primary px-4 font-semibold text-white transition-colors hover:bg-primary-hover"
        aria-label={t('operations.new')}
      >
        <Plus className="size-5" strokeWidth={2.25} />
        <span className="hidden xl:inline">{t('operations.new')}</span>
      </MenuButton>
      <MenuPanel className="w-80">
        {available.map((shortcut) => (
          <MenuEntry
            key={shortcut.key}
            icon={<shortcut.icon />}
            onClick={() => void navigate(shortcutPath(shortcut))}
          >
            <span className="block font-medium">{t(`operations.${shortcut.key}.title`)}</span>
            <span className="block text-sm text-ink-2">{t(`operations.${shortcut.key}.text`)}</span>
          </MenuEntry>
        ))}
      </MenuPanel>
    </Menu>
  );
}

/** Notification centre (ALR-02): the bell with the new alerts. */
export function NotificationsButton() {
  return <AlertsBell className={cn(topButton, 'w-12 justify-center px-0')} />;
}

/** Interface language. Saved to the user's profile so it follows them everywhere (USR-10). */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const { setUser } = useAuth();
  const context = useAppContext();
  const errorText = useErrorText();
  const languages = context?.enabledLanguages ?? (['en', 'fr'] as Language[]);

  const change = async (language: Language) => {
    try {
      setUser(await authApi.updateProfile({ language }));
      await setLanguage(language);
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  return (
    <Menu>
      <MenuButton className={topButton} aria-label={t('language.label')}>
        <Globe className="size-5 text-ink-2" strokeWidth={1.75} />
        <span className="font-semibold uppercase">{i18n.language}</span>
      </MenuButton>
      <MenuPanel className="min-w-44">
        {languages.map((language) => (
          <MenuEntry
            key={language}
            onClick={() => void change(language)}
            icon={i18n.language === language ? <Check /> : <span className="size-[18px]" />}
          >
            <span lang={language}>{LANGUAGE_NATIVE_NAMES[language]}</span>
          </MenuEntry>
        ))}
      </MenuPanel>
    </Menu>
  );
}

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const user = useCurrentUser();

  return (
    <Menu>
      <MenuButton className="flex items-center gap-3 rounded-control p-1 pr-2 text-left hover:bg-ground data-open:bg-ground">
        <Avatar name={user.fullName} seed={user.username} />
        <span className="hidden min-w-0 xl:block">
          <span className="block max-w-36 truncate font-semibold leading-tight">
            {user.fullName}
          </span>
          <span className="block text-sm text-muted">{t(`roles.${user.role}`)}</span>
        </span>
      </MenuButton>
      <MenuPanel>
        <div className="px-3 py-2">
          <p className="font-semibold">{user.fullName}</p>
          <p className="font-mono text-caption text-muted">{user.username}</p>
        </div>
        <MenuDivider />
        <MenuEntry icon={<UserRound />} onClick={() => void navigate('/profile')}>
          {t('nav.profile')}
        </MenuEntry>
        <MenuEntry
          icon={<LogOut />}
          onClick={() => {
            void signOut().then(() => navigate('/sign-in', { replace: true }));
          }}
        >
          {t('actions.signOut')}
        </MenuEntry>
      </MenuPanel>
    </Menu>
  );
}
